import React from "react";
import styled from "styled-components";

const BracketContainer = styled.div`
  display: flex;
  gap: 4rem;
  overflow-x: auto;
  padding: 2rem 1rem;
`;

const RoundColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  min-width: 20rem;
`;

const MatchCard = styled.div<{ $active?: boolean; $clickable?: boolean }>`
  background: var(--color-white);
  border: 2px solid var(--color-black);
  border-radius: 1rem;
  padding: 1rem;
  margin: 1.5rem 0;
  box-shadow: ${({ $active }) => ($active ? "0 0 1.5rem var(--color-purple)" : "none")};
  border-color: ${({ $active }) => ($active ? "var(--color-purple)" : "var(--color-black)")};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: transform 0.1s;

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? "translateY(-2px)" : "none")};
  }
`;

const PlayerRow = styled.div<{ $winner?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${({ $winner }) => ($winner ? "bold" : "normal")};
  color: ${({ $winner }) => ($winner ? "var(--color-black)" : "rgba(0,0,0,0.6)")};
  margin: 0.4rem 0;
`;

const Score = styled.span`
  background: #eee;
  padding: 0.2rem 0.6rem;
  border-radius: 0.4rem;
  font-size: 1.2rem;
`;

const StatusTag = styled.span<{ $status: string }>`
  display: inline-block;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.2rem 0.6rem;
  border-radius: 0.4rem;
  margin-top: 0.4rem;
  color: ${({ $status }) =>
    $status === "ACTIVE"
      ? "var(--color-white)"
      : $status === "COMPLETED"
        ? "#065f46"
        : "#6b7280"};
  background: ${({ $status }) =>
    $status === "ACTIVE"
      ? "var(--color-purple)"
      : $status === "COMPLETED"
        ? "#dcfce7"
        : "#f3f4f6"};
`;

interface TournamentBracketProps {
  league: any;
  onSelectMatch?: (seriesId: string) => void;
}

const TournamentBracket: React.FC<TournamentBracketProps> = ({ league, onSelectMatch }) => {
  const roundTitles = ["Round of 32", "Sweet 16", "Elite 8", "Final Four", "Championship"];

  const getMatchesForRound = (round: number) => {
    return (league.series ?? []).filter((s: any) => s.round === round);
  };

  return (
    <BracketContainer>
      {[1, 2, 3, 4, 5].map((round) => {
        const matches = getMatchesForRound(round);
        if (matches.length === 0 && round > (league.round ?? 0)) return null;

        return (
          <RoundColumn key={round}>
            <h4
              style={{
                textAlign: "center",
                marginBottom: "2rem",
                textTransform: "uppercase",
                fontSize: "1.2rem",
                letterSpacing: "0.1em",
              }}
            >
              {roundTitles[round - 1]}
            </h4>
            {matches.map((match: any) => {
              const clickable =
                !!onSelectMatch && (match.status === "ACTIVE" || match.status === "COMPLETED");
              return (
                <MatchCard
                  key={match.id}
                  $active={match.status === "ACTIVE"}
                  $clickable={clickable}
                  onClick={clickable ? () => onSelectMatch!(match.id) : undefined}
                >
                  <PlayerRow $winner={match.winnerId === match.p1Id}>
                    <span>{match.p1?.name || "TBD"}</span>
                    <Score>{match.score1}</Score>
                  </PlayerRow>
                  <div style={{ height: "1px", background: "#eee", margin: "0.4rem 0" }} />
                  <PlayerRow $winner={match.winnerId === match.p2Id}>
                    <span>{match.p2?.name || "TBD"}</span>
                    <Score>{match.score2}</Score>
                  </PlayerRow>
                  {match.status && <StatusTag $status={match.status}>{match.status}</StatusTag>}
                </MatchCard>
              );
            })}
          </RoundColumn>
        );
      })}
    </BracketContainer>
  );
};

export default TournamentBracket;
